import {
  Component,
  inject,
  OnInit,
  computed,
  DestroyRef,
  signal,
  ViewChild,
  ElementRef,
  AfterViewChecked, effect, untracked
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {CopilotPromptBarComponent} from '../../components/copilot-prompt-bar/copilot-prompt-bar.component';
import {FormControl} from '@angular/forms';
import {ChatMessage, ChatMessageAuthor, UserMessageResponse} from '../../copilot.model';
import {UserMessageComponent} from "../../components/user-message/user-message.component";
import {CopilotMessageComponent} from "../../components/copilot-message/copilot-message.component";
import {EmptyChatMessageComponent} from '../../components/empty-chat-message/empty-chat-message.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ChatLibraryService} from '../../chat-library/services/chat-library.service';
import {PolarisRoutePaths, RoutePaths} from '../../../../shared/types/common';
import {TwoStageBrandPickerComponent} from '../../components/two-stage-brand-picker/two-stage-brand-picker.component';
import {DashboardService} from '../../../../services/dashboard.service';
import {PolarisRuntimeFacadeService} from '../../../../services/polaris/polaris-runtime-facade.service';
import {mapPolarisMessagesToChatMessages} from '../../adapters/polaris-message-to-chat-message.mapper';
import {AuthorizationService} from '../../../../services/authorization.service';
import {Permissions} from '../../../../shared/permissions';
import {ChatViewType, PolarisModelVersion} from '../../../../services/polaris/model';
import {AssetVersionPipe} from '../../../../pipes/asset-version.pipe';
import {StateService} from '../../../../services/state.service';
import {UtilService} from '../../../../services/util.service';
import {ShareChatService} from '../../components/share-chat-modal/share-chat.service';
import {PolarisStateService} from '../../../../services/polaris/polaris-state.service';
import {ChatNotFoundComponent} from '../../components/chat-not-found/chat-not-found.component';
import {PosthogService} from '../../../../services/posthog.service';
import {PosthogEventCode, PosthogEventName} from '../../../../services/types';
import {
  ChatLibraryDrawerComponent
} from '../../chat-library/components/chat-library-drawer/chat-library-drawer.component';

@Component({
  selector: 'app-copilot-chat-screen',
  imports: [CopilotPromptBarComponent, UserMessageComponent, CopilotMessageComponent, EmptyChatMessageComponent, TwoStageBrandPickerComponent, ChatLibraryDrawerComponent, AssetVersionPipe, ChatNotFoundComponent],
  templateUrl: './copilot-chat-screen.component.html',
  styleUrl: './copilot-chat-screen.component.scss'
})

export class CopilotChatScreenComponent implements OnInit, AfterViewChecked {
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);
  private chatLibraryService = inject(ChatLibraryService);
  readonly isLibraryOpen = computed(() => this.chatLibraryService.state().isOpen);
  private authorizationService = inject(AuthorizationService);
  private dashboardService = inject(DashboardService);
  private route = inject(ActivatedRoute);
  private destroy$ = inject(DestroyRef);
  private router = inject(Router);
  private stateService = inject(StateService);
  private utilService = inject(UtilService);
  private shareChatService = inject(ShareChatService);
  private polarisStateService = inject(PolarisStateService);
  private location = inject(Location);
  private posthogService = inject(PosthogService);
  public isCopyInFlight = this.polarisStateService.isCopyInFlight;
  public isSharedView = false;
  private sharedNoticeShownFor: string | null = null;
  private sharedChatMessageCount = 0;
  public forkBoundaryIndex = signal<number | null>(null);
  public chatNotFound = signal(false);
  public chatId = signal<string | null>(null);
  public canSelectV2 = this.authorizationService.hasPermissions([Permissions.SELECT_POLARIS_MODEL_VERSION]);

  private chatVm = computed(() => {
    const id = this.chatId();
    if (!id) return undefined;
    return this.polarisRuntimeFacadeService.state.chats()[id];
  });

  public chatModelVersion = computed<PolarisModelVersion>(() => this.chatVm()?.modelVersion ?? 'v2');

  public isShareableChat = computed<boolean>(() => this.chatModelVersion() !== 'v1');

  public isChatShared = computed<boolean>(() => this.chatVm()?.isShared === true);

  public myBrand = computed<string | undefined>(() => {
    const raw = this.chatVm()?.myBrand;
    if (raw == null) return undefined;
    const allowed = this.dashboardService.brandsOptions().myBrands?.some(brand => brand.key === raw);
    return allowed ? raw : undefined;
  });

  public myCompetitors = computed<string[]>(() => this.chatVm()?.myCompetitors ?? []);
  public responseOverrides = signal<Record<string, UserMessageResponse>>({});
  public userHasInteracted = signal(false);
  private lastAmountOfMessages = 0;
  private routeLoadVersion = 0;
  @ViewChild('scrollAnchor') scrollAnchor!: ElementRef;

  constructor() {
    effect(() => {
      const view = this.polarisStateService.chatViewState();
      if (view.mode === ChatViewType.SHARED && view.copyInFlight) {
        this.forkBoundaryIndex.set(this.sharedChatMessageCount);
        return;
      }
      if (view.mode !== ChatViewType.COPIED) {
        return;
      }
      const forkedChatId = this.polarisRuntimeFacadeService.state.activeChat()?.id;
      if (forkedChatId && forkedChatId !== this.chatId()) {
        this.isSharedView = false;
        this.chatId.set(forkedChatId);
        this.forkBoundaryIndex.set(this.sharedChatMessageCount);
        this.location.go(`/${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${forkedChatId}`);
        this.polarisRuntimeFacadeService.setRecentChatId(forkedChatId);
      }
    });

    const locationSub = this.location.subscribe((event) => this.handleLocationPopState(event.url ?? ''));
    this.destroy$.onDestroy(() => locationSub.unsubscribe());
  }

  promptControl = new FormControl('');
  messages = computed<ChatMessage[]>(() => {
    const currentChatId = this.chatId();
    if (!currentChatId) return [];
    const overrides = this.responseOverrides();
    return mapPolarisMessagesToChatMessages(this.polarisRuntimeFacadeService.getChatMessages(currentChatId)())
      .map((message) => {
        return {
          ...message,
          userResponse:
            overrides[message.id] ?? message.userResponse
        }
      });
  });
  hasChatLibrary = computed(() =>
    this.messages().some(message => !!message.chatLibrary || !!message.creativesURLs?.length)
  );

  showEmptyState = computed(() => {
    const vm = this.chatVm();
    return vm != null &&
      this.messages().length === 0 &&
      !vm.isNewlyCreated &&
      !this.userHasInteracted() &&
      !this.polarisRuntimeFacadeService.state.isLoadingActiveChat();
  });

  ngOnInit(): void {
    this.isSharedView = this.route.snapshot.data['sharedView'] === true;
    this.route.params.pipe(takeUntilDestroyed(this.destroy$)).subscribe(async params => {
      const newChatId = params['id'];
      const routeLoadVersion = ++this.routeLoadVersion;
      this.chatLibraryService.resetLibrary();
      this.chatId.set(newChatId);
      this.userHasInteracted.set(false);
      this.chatNotFound.set(false);
      this.forkBoundaryIndex.set(null);
      this.sharedChatMessageCount = 0;

      if (!newChatId) {
        this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
        return;
      }
      if (this.isSharedView) {
        this.polarisStateService.enterSharedView(newChatId);
      } else {
        this.polarisStateService.enterOwnedView();
        this.polarisRuntimeFacadeService.setRecentChatId(newChatId);
      }
      const accountBeforeLoad = this.stateService.activeAccount();
      try {
        const isLoaded = this.isSharedView
          ? await this.polarisRuntimeFacadeService.ensureSharedChatLoaded(newChatId)
          : await this.polarisRuntimeFacadeService.ensureChatLoaded(newChatId);
        if (this.isStaleRouteLoad(routeLoadVersion, newChatId)) {
          return;
        }
        if (!isLoaded) {
          this.showChatNotFound();
          return;
        }
        if (this.stateService.activeAccount() !== accountBeforeLoad) {
          this.dashboardService.refreshFilters().pipe(takeUntilDestroyed(this.destroy$)).subscribe();
        }
        if (this.isSharedView) {
          this.sharedChatMessageCount = this.messages().length;
          const ownerUserId = this.polarisStateService.getChatById(newChatId)?.overview.userId;
          this.posthogService.capture(PosthogEventName.SHARE_LINK_OPENED, {
            [PosthogEventCode.IS_OWNER]: ownerUserId === this.authorizationService.userProfile.id
          });
          if (this.sharedNoticeShownFor !== newChatId) {
            this.sharedNoticeShownFor = newChatId;
            this.notifyViewingAsAccount();
          }
        }
      } catch {
        if (this.isStaleRouteLoad(routeLoadVersion, newChatId)) {
          return;
        }
        this.showChatNotFound();
      }
    });
  }

  ngAfterViewChecked() {
    if (this.lastAmountOfMessages !== this.messages().length)
      this.scrollToAnchor();
    this.lastAmountOfMessages = this.messages().length;
  }

  async onSendMessage(message: string): Promise<void> {
    const currentChatId = this.chatId();
    if (!currentChatId) return;
    this.userHasInteracted.set(true);
    const result = await this.polarisRuntimeFacadeService.sendMessage(currentChatId, message);
    if (result.status === 'chat_not_found') {
      await this.navigateToPolarisHome();
    }
  }

  async onRetryRequested(event: { chatId: string; interactionId?: string }): Promise<void> {
    const currentChatId = this.chatId();
    if (!currentChatId) {
      return;
    }
    if (!event.interactionId) {
      return;
    }
    const userPrompt = this.messages()
      .find((message) => message.author === ChatMessageAuthor.USER && message.interactionId === event.interactionId)
      ?.content;
    if (!userPrompt) {
      return;
    }
    const result = await this.polarisRuntimeFacadeService.sendMessage(currentChatId, userPrompt);
    if (result.status === 'chat_not_found') {
      await this.navigateToPolarisHome();
    }
  }

  onFollowUpChatNotFound(): void {
    void this.navigateToPolarisHome();
  }

  private showChatNotFound(): void {
    this.chatNotFound.set(true);
    this.polarisRuntimeFacadeService.resetRecentChatId();
    if (this.isSharedView) {
      this.posthogService.capture(PosthogEventName.SHARE_LINK_ACCESS_DENIED);
    }
  }

  public async navigateToPolarisHome(): Promise<void> {
    await this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
  }

  onResponseSelected(event: { messageId: string; response: UserMessageResponse }): void {
    this.responseOverrides.update((overrides) => ({
      ...overrides,
      [event.messageId]: event.response
    }));
  }

  isLatestAssistantMessage(message: ChatMessage): boolean {
    const latestAssistantMessage = [...this.messages()]
      .reverse()
      .find((candidate) => candidate.author === ChatMessageAuthor.ASSISTANT);
    return latestAssistantMessage?.id === message.id;
  }

  private scrollToAnchor() {
    if (this.scrollAnchor) {
      this.scrollAnchor.nativeElement.scrollIntoView({behavior: 'smooth'});
    }
  }

  private isStaleRouteLoad(routeLoadVersion: number, expectedChatId: string): boolean {
    return routeLoadVersion !== this.routeLoadVersion || this.chatId() !== expectedChatId;
  }

  private handleLocationPopState(url: string): void {
    const sharedPrefix = `/${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${PolarisRoutePaths.SHARED}/`;
    if (this.isSharedView || !url.startsWith(sharedPrefix)) {
      return;
    }
    const sharedChatId = url.slice(sharedPrefix.length).split(/[?#]/)[0];
    if (sharedChatId) {
      void this.restoreSharedView(sharedChatId);
    }
  }

  private async restoreSharedView(sharedChatId: string): Promise<void> {
    const routeLoadVersion = ++this.routeLoadVersion;
    this.isSharedView = true;
    this.chatId.set(sharedChatId);
    this.userHasInteracted.set(false);
    this.chatNotFound.set(false);
    this.forkBoundaryIndex.set(null);
    this.sharedChatMessageCount = 0;
    this.polarisStateService.enterSharedView(sharedChatId);
    try {
      const isLoaded = await this.polarisRuntimeFacadeService.ensureSharedChatLoaded(sharedChatId);
      if (this.isStaleRouteLoad(routeLoadVersion, sharedChatId)) {
        return;
      }
      if (!isLoaded) {
        this.showChatNotFound();
        return;
      }
      this.sharedChatMessageCount = this.messages().length;
    } catch {
      if (this.isStaleRouteLoad(routeLoadVersion, sharedChatId)) {
        return;
      }
      this.showChatNotFound();
    }
  }

  openShareModal(): void {
    const currentChatId = this.chatId();
    if (!currentChatId) {
      return;
    }
    this.shareChatService.open(currentChatId);
  }

  private notifyViewingAsAccount(): void {
    const accountKey = this.stateService.activeAccount();
    const accountName = this.stateService.accounts.find(account => account.key === accountKey)?.name ?? accountKey;
    this.utilService.openSnackBar(`You're now viewing as ${accountName}`, {
      variant: 'success',
      position: 'center',
      className: 'shared-view-toast',
      duration: 3000
    });
  }
}
